/**
 * Dialogs System
 *
 * This module provides a global dialog/modal management system.
 * It allows any component in the app to open dialogs without prop drilling.
 *
 * Architecture:
 * - DialogsProvider: Wraps app and provides context
 * - useDialogsActions: Hook to open/close dialogs
 * - useDialogsForm: Hook to manage form state within dialogs
 *
 * Usage - Opening a Dialog:
 * ```jsx
 * import { useDialogsActions } from 'datastage/components/Dialogs/hooks';
 *
 * function MyComponent() {
 *   const { openDialog, closeDialog } = useDialogsActions();
 *
 *   const handleClick = () => {
 *     openDialog({
 *       title: 'Confirm Action',
 *       content: <p>Are you sure?</p>,
 *       onConfirm: () => {
 *         // Handle confirmation
 *         closeDialog();
 *       },
 *     });
 *   };
 *
 *   return <button onClick={handleClick}>Open Dialog</button>;
 * }
 * ```
 *
 * Pre-built Dialog Hooks:
 * - useDeleteDialog: Confirmation dialog for delete actions
 * - useUploadDialog: File upload dialog
 *
 * @see src/datastage/components/Dialogs/hooks.jsx - Dialog action hooks
 * @see src/datastage/components/Dialogs/forms.js - Form state management
 * @see src/datastage/components/Dialogs/dialogs/ - Pre-built dialog types
 */
import React from 'react';
import { useDialogsCtxBuilder } from './hooks';
import { useDialogsFormCtxBuilder } from './forms';
import { DialogsCtx } from './context';
import { Dialog } from './dialog';

/**
 * DialogsProvider Component
 *
 * Wrap your app (or a section) with this provider to enable the dialog system.
 * Typically placed in the router to cover all authenticated pages.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const DialogsProvider = ({ children }) => {
    // Build dialog state and actions
    const ctx = useDialogsCtxBuilder();

    // Build form state management for dialogs
    const form = useDialogsFormCtxBuilder();

    return (
        <DialogsCtx.Provider value={{ ...ctx, form }}>
            {children}
            {/* Render all active dialogs */}
            {ctx.dialogs.map((dialog) => (
                <Dialog key={dialog.uid} {...dialog} />
            ))}
        </DialogsCtx.Provider>
    );
};
